import {OnlinePaymentStatus, PaymentsStatus, POSPaymentStatus} from '~/code/constants'
import {FilterConfig, StoreOption, SummaryItem} from '../models'
import {PaymentsStatuses} from './generators'
import i18next from 'i18next'

export const findItemByStatus = (data, status) => data?.find((el) => el.status === status)

export const calculatePercent = (currAmount, prevAmount) => {
    if (prevAmount > 0 && currAmount > 0) {
        return ((currAmount - prevAmount) / prevAmount) * 100
    }
    return 0
}

/**
 * Helper function to merge items with the same status by summing amounts and counts
 */
const mergeByStatus = (filteredData: SummaryItem[]): SummaryItem[] => {
    const mergedMap = new Map()
    for (const item of filteredData) {
        const existing = mergedMap.get(item.status)
        if (existing) {
            existing.amount += item.amount ?? 0
            existing.count += item.count ?? 0
        } else {
            mergedMap.set(item.status, {...item})
        }
    }
    return Array.from(mergedMap.values())
}

/**
 * Helper function to map merged data to PaymentsStatuses array with zero values for missing statuses
 */
const mapToPaymentsStatuses = (mergedData: SummaryItem[]): SummaryItem[] => {
    return PaymentsStatuses.map((status) => {
        const currentItem = findItemByStatus(mergedData, status)

        if (!currentItem) {
            return {
                status,
                count: 0,
                amount: 0
            }
        }

        return currentItem
    })
}

/**
 * Generic function to filter and transform payment statuses
 */
const filterByStatuses = (data: SummaryItem[], config: FilterConfig): SummaryItem[] => {
    const {relevantStatuses, statusMappings} = config

    const filteredData = data
        .filter(({status}) => relevantStatuses.includes(status))
        .map((transaction) => {
            let mappedStatus = transaction.status

            for (const mapping of statusMappings) {
                const fromStatuses = Array.isArray(mapping.from) ? mapping.from : [mapping.from]
                if (fromStatuses.includes(transaction.status)) {
                    mappedStatus = mapping.to
                    break
                }
            }

            return {...transaction, status: mappedStatus}
        })

    const mergedData = mergeByStatus(filteredData)
    return mapToPaymentsStatuses(mergedData)
}

export const filterECOMByStatuses = (data) => {
    return filterByStatuses(data, {
        relevantStatuses: [
            OnlinePaymentStatus.charge,
            OnlinePaymentStatus.refund,
            OnlinePaymentStatus.credited,
            OnlinePaymentStatus.auth
        ],
        statusMappings: [
            {from: OnlinePaymentStatus.charge, to: PaymentsStatus.charged},
            {
                from: [OnlinePaymentStatus.refund, OnlinePaymentStatus.credited],
                to: PaymentsStatus.refunds
            },
            {from: OnlinePaymentStatus.auth, to: PaymentsStatus.pending}
        ]
    })
}

export const filterPOSByStatuses = (data) => {
    return filterByStatuses(data, {
        relevantStatuses: [
            POSPaymentStatus.charged,
            POSPaymentStatus.authorised,
            POSPaymentStatus.refunded
        ],
        statusMappings: [
            {from: POSPaymentStatus.charged, to: PaymentsStatus.charged},
            {from: POSPaymentStatus.refunded, to: PaymentsStatus.refunds},
            {from: POSPaymentStatus.authorised, to: PaymentsStatus.pending}
        ]
    })
}

export const getDefaultAllStoresOption = (): StoreOption => ({
    value: 'all',
    label: i18next.t('PaymentsTransactionsFilter.allStores'),
    type: 'all',
    selectLabel: i18next.t('PaymentsTransactionsFilter.allStores')
})
